import { useRef, useState, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, RotateCcw, ArrowRight } from "lucide-react";
import { HERO_VIDEO_SRC, openWebinar } from "@/data/content";

export const HeroVideo = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [started, setStarted] = useState(false);

  const safePlay = (v) => {
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  };

  const toggle = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) safePlay(v);
    else v.pause();
  }, []);

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    setEnded(false);
    safePlay(v);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-black glow-blue">
      <div className="relative aspect-[4/5] w-full">
        <video
          ref={videoRef}
          data-testid="hero-video"
          src={HERO_VIDEO_SRC}
          poster="/media/founder-poster.png"
          playsInline
          preload="metadata"
          className="h-full w-full object-contain bg-black"
          onClick={toggle}
          onPlay={() => { setPlaying(true); setEnded(false); setStarted(true); }}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); setEnded(true); }}
        />

        {/* poster thumbnail (before first play) */}
        {!started && !ended && (
          <img
            src="/media/founder-poster.png"
            alt="Kolja – Kurier Connect"
            data-testid="hero-video-poster"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        )}

        {/* play overlay (initial / paused) */}
        {!playing && !ended && (
          <button
            data-testid="hero-video-play"
            onClick={toggle}
            className="group absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/20"
            aria-label="Video abspielen"
          >
            <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-brand-green text-brand-bg transition-transform duration-300 group-hover:scale-110">
              {!started && <span className="absolute inset-0 rounded-full bg-brand-green/40 animate-ping" />}
              <Play className="relative ml-1 h-8 w-8 fill-brand-bg" />
            </span>
            {!started && (
              <span className="rounded-full bg-black/50 px-4 py-1.5 font-heading text-sm font-semibold text-white backdrop-blur-sm">
                Kurze Vorstellung ansehen
              </span>
            )}
          </button>
        )}

        {/* end screen with CTA */}
        {ended && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-black/75 px-6 text-center backdrop-blur-sm">
            <p className="font-heading text-xl font-bold text-white">Bereit für den Einstieg?</p>
            <button
              data-testid="hero-video-cta"
              onClick={openWebinar}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide text-brand-bg glow-green-sm"
            >
              Kostenlos zum Webinar anmelden
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
            </button>
            <button
              onClick={replay}
              data-testid="hero-video-replay"
              className="inline-flex items-center gap-2 font-body text-sm text-white/70 transition-colors hover:text-white"
            >
              <RotateCcw className="h-4 w-4" /> Noch einmal ansehen
            </button>
          </div>
        )}

        {/* mute toggle (while playing) */}
        {playing && (
          <button
            onClick={toggleMute}
            data-testid="hero-video-mute"
            aria-label={muted ? "Ton an" : "Ton aus"}
            className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        )}

        {/* pause (while playing) */}
        {playing && (
          <button
            onClick={toggle}
            data-testid="hero-video-pause"
            aria-label="Pause"
            className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75"
          >
            <Pause className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
